import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { setUserSelectedRegionId } from '@sportworld/js-sdk/src/actions/region'

import { updateAppConfig } from '@/actions'
import { Button } from '@/components/Button/Button'
import { LineLoader } from '@/components/LinesGroup'
import { Select } from '@/components/Select/select'
import { SettingsHeader } from '@/components/SettingsHeader'
import { focusIds } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { useFetchAvailableRegions } from '@/hooks/useFetchAvailableRegions'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { ApiService } from '@/service'
import useStore from '@/store/useStore'

import {
  ContentContainer,
  HeaderElement,
  Title,
  Description,
  CardEl,
  ButtonWrapper,
  CustomLoader,
} from './styles'
import { SettingsContainer } from '../styles'

const focusId = `${focusIds.SETTINGS_REGION}${focusIds.CONTAINER_MOD}`

const RegionRaw: FC = () => {
  const { t } = useCommonTranslation()
  const regionId = useStore((state) => state.common.appConfig.regionId)
  const [currentRegionName, setCurrentRegionName] = useState('')
  const {
    loading,
    availableRegionOptions,
    selectedRegionId,
    error,
    requestAction,
    setSelectedRegionId,
  } = useFetchAvailableRegions()

  useEffect(() => {
    regionId && getRegionName(regionId)
  }, [regionId])

  const onSelectedHandler = useCallback(
    async (selectedId: string) => {
      setUserSelectedRegionId(selectedId)
      setSelectedRegionId(selectedId)
      // await updateActiveRegionFromBackend()
      await updateAppConfig(selectedId)
    },
    [updateAppConfig, setUserSelectedRegionId],
  )

  const getRegionName = useCallback(
    async (regionId: string) => {
      let regionName = availableRegionOptions.find(
        (option) => option.id === regionId,
      )?.label
      if (!regionName) {
        try {
          const region = await ApiService.getRegionById(regionId)
          regionName = region.name
        } catch {}
      }
      setCurrentRegionName(regionName || regionId)
    },
    [availableRegionOptions],
  )

  const renderSelect = useCallback(() => {
    return (
      <ButtonWrapper>
        <Select
          selectOptions={{
            title: t('availableRegions'),
            description: t('selectRegion'),
            selectOptionList: availableRegionOptions,
            selectedOptionId: selectedRegionId,
            onSelect: onSelectedHandler,
          }}
          actionComponent={
            <Button
              iconSrc={icons.navigation.arrowRight}
              text={t('change')}
              onClick={() => {}}
            />
          }
        />
      </ButtonWrapper>
    )
  }, [availableRegionOptions, selectedRegionId, onSelectedHandler])

  const renderedSelect = useMemo(
    () =>
      loading ? (
        <CustomLoader>
          <LineLoader error={error} action={requestAction} />
        </CustomLoader>
      ) : availableRegionOptions.length ? (
        renderSelect()
      ) : (
        <Description>{t('noRegions')}</Description>
      ),
    [loading, error, availableRegionOptions, requestAction, renderSelect],
  )

  return (
    <SettingsContainer focusId={focusId} elementType="div">
      <SettingsHeader title={t('region')} />
      <ContentContainer>
        <CardEl>
          <HeaderElement>
            <Title>{currentRegionName}</Title>
            <Description>{t('regionCardText')}</Description>
          </HeaderElement>
          {renderedSelect}
        </CardEl>
      </ContentContainer>
    </SettingsContainer>
  )
}

export const Region = memo(RegionRaw)
